import type { RegistrationProgressResponse } from './status-display';

export type RegistrationCorrectionStep =
  | 'business-selection'
  | 'company'
  | 'personal'
  | 'kyb-npwp'
  | 'kyb-ktp-scan'
  | 'kyb-selfie-scan'
  | 'kyb-ktp-intro'
  | 'restaurant-category'
  | 'outlet-info'
  | 'outlet-delivery'
  | 'outlet-delivery-preview'
  | 'outlet-delivery-branch';

const OUTLET_RESTAURANT_CATEGORY_FIELD = 'cg_3_business_type';
const OUTLET_INFO_FIELDS = new Set([
  'name',
  'phone',
  'email',
  'address',
  'latitude',
  'longitude',
  'social_media',
  'online_order_merchant_link',
]);

export function resolveOutletFieldsResumeStep(
  missingFields: string[]
): RegistrationCorrectionStep {
  const hasCategoryMissing = missingFields.includes(OUTLET_RESTAURANT_CATEGORY_FIELD);
  const hasOutletInfoMissing = missingFields.some((field) =>
    OUTLET_INFO_FIELDS.has(field)
  );

  if (hasCategoryMissing) {
    return 'restaurant-category';
  }

  if (hasOutletInfoMissing) {
    return 'outlet-info';
  }

  return 'outlet-info';
}

function resolveBusinessDocumentsDraftStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  const { sections } = progress;
  const missingDocuments = sections.business.missing_documents;

  if (missingDocuments.length === 0) {
    return null;
  }

  const ownership = sections.business.ownership_type;

  if (ownership === 'company') {
    if (missingDocuments.includes('company_npwp')) return 'kyb-npwp';
    if (missingDocuments.includes('pic_ktp')) return 'kyb-ktp-scan';
    if (missingDocuments.includes('pic_selfie_ktp')) return 'kyb-selfie-scan';
    return 'kyb-npwp';
  }

  if (missingDocuments.includes('owner_ktp')) return 'kyb-ktp-scan';
  if (missingDocuments.includes('owner_selfie_ktp')) return 'kyb-selfie-scan';
  return 'kyb-ktp-intro';
}

function resolveBusinessFieldsDraftStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  const { sections } = progress;

  if (sections.business.missing_fields.length === 0) {
    return null;
  }

  return sections.business.ownership_type === 'company' ? 'company' : 'personal';
}

function resolveDraftOutletSectionStep(
  progress: RegistrationProgressResponse,
  outletId: string,
  outletIndex: number
): RegistrationCorrectionStep | null {
  const outletSection = progress.sections.outlets.per_outlet.find(
    (section) => section.outlet_id === outletId
  );

  if (!outletSection) {
    return null;
  }

  if (outletSection.complete) {
    return 'outlet-delivery-preview';
  }

  if (!outletSection.fields_complete) {
    return resolveOutletFieldsResumeStep(outletSection.missing_fields);
  }

  return outletIndex === 0 ? 'outlet-delivery' : 'outlet-delivery-branch';
}

function hasOutletCorrectionItems(
  outletSection: RegistrationProgressResponse['sections']['outlets']['per_outlet'][number]
): boolean {
  return (
    outletSection.missing_fields.length > 0 ||
    outletSection.missing_attachments.length > 0
  );
}

function resolvePendingReviewBusinessCorrectionStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  return resolveBusinessFieldsDraftStep(progress);
}

function resolvePendingReviewBusinessDocumentsCorrectionStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  return resolveBusinessDocumentsDraftStep(progress);
}

function resolvePendingReviewOutletCorrectionStep(
  progress: RegistrationProgressResponse,
  outletId: string,
  outletIndex: number
): RegistrationCorrectionStep | null {
  const outletSection = progress.sections.outlets.per_outlet.find(
    (section) => section.outlet_id === outletId
  );

  if (!outletSection || !hasOutletCorrectionItems(outletSection)) {
    return null;
  }

  if (!outletSection.fields_complete) {
    return resolveOutletFieldsResumeStep(outletSection.missing_fields);
  }

  return outletIndex === 0 ? 'outlet-delivery' : 'outlet-delivery-branch';
}

export function resolveBusinessSectionStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  if (progress.registration_status === 'pending_review') {
    return resolvePendingReviewBusinessCorrectionStep(progress);
  }

  if (!progress.sections.business.saved) {
    return 'business-selection';
  }

  if (!progress.sections.business.complete) {
    return resolveBusinessFieldsDraftStep(progress);
  }

  return null;
}

export function resolveBusinessDocumentsSectionStep(
  progress: RegistrationProgressResponse
): RegistrationCorrectionStep | null {
  if (progress.registration_status === 'pending_review') {
    return resolvePendingReviewBusinessDocumentsCorrectionStep(progress);
  }

  if (!progress.sections.business.saved || progress.sections.business.complete) {
    return null;
  }

  return resolveBusinessDocumentsDraftStep(progress);
}

export function resolveOutletSectionStep(
  progress: RegistrationProgressResponse,
  outletId: string,
  outletIndex: number
): RegistrationCorrectionStep | null {
  if (progress.registration_status === 'pending_review') {
    return resolvePendingReviewOutletCorrectionStep(
      progress,
      outletId,
      outletIndex
    );
  }

  return resolveDraftOutletSectionStep(progress, outletId, outletIndex);
}
